import { allFields } from '@/lib/formSections'
import { isMultiFileAttachment, supportsAttachment } from '@/types/cgt'
import type { LisPdfMapping, LisPdfMappingBody, LisReleaseDocument } from '@/api/lisResultsApi'
import { RESULT_CODE_PATTERN } from '@/lib/lis/resultMapping'
import {
  EMPTY_PROVIDER,
  providerDraftBody,
  providerDraftOf,
  providerProblem,
  type ProviderDraft,
} from '@/lib/lis/orderingProvider'

type DefinitionLike = Parameters<typeof allFields>[0]

/**
 * PDF outgoing mappings and the PDF in Release to Epic (z8nrz7fgcp, MedStar
 * test 013 as a PDF).
 *
 * They mirror the backend's `lis-pdf-document.ts`, so a mistake is shown beside
 * the field instead of coming back as a 400. The backend still decides, and it
 * is the one that reads the file: the preview's `problem` is its answer.
 */

/** OBX-3.2, the relay's limit. */
export const PDF_TEXT_MAX = 199

/**
 * The fields a PDF can be attached to: the multi-file Attachment type, and any
 * field with the attachment add-on on. Not an Image field, which takes
 * pictures only.
 */
export function pdfFieldOptions(def: DefinitionLike | undefined): { id: string; label: string }[] {
  if (!def) return []
  return allFields(def)
    .filter((f) => isMultiFileAttachment(f.fieldType) || (supportsAttachment(f.fieldType) && !!f.attachment?.enabled))
    .map((f) => ({ id: f.id, label: f.label }))
}

/** A PDF mapping as typed into the add / edit form. */
export interface PdfMappingDraft {
  /** OBR-4.1, the order code the PDF is filed under. Typed: nothing is ordered. */
  testCode: string
  testName: string
  formDefinitionId: string
  fieldId: string
  code: string
  text: string
  /** OBR-16 for this PDF's sends (z8nrz7fdc0). */
  provider: ProviderDraft
}

export function emptyPdfDraft(): PdfMappingDraft {
  // MedStar's sample (test 013): CTPROCRPT^CELL THERAPY PROCESSING REPORT, one CTPDF line.
  return {
    testCode: 'CTPROCRPT',
    testName: 'CELL THERAPY PROCESSING REPORT',
    formDefinitionId: '',
    fieldId: '',
    code: 'CTPDF',
    text: 'CELL THERAPY REPORT (PDF)',
    provider: { ...EMPTY_PROVIDER },
  }
}

export function pdfDraftFrom(m: LisPdfMapping): PdfMappingDraft {
  return {
    testCode: m.testCode,
    testName: m.testName ?? '',
    formDefinitionId: m.formDefinitionId,
    fieldId: m.fieldId,
    code: m.code,
    text: m.text ?? '',
    provider: providerDraftOf(m.orderingProvider),
  }
}

/**
 * Why the draft cannot be saved, by field (AC1.3). `pdfTests` are the order
 * codes other PDF mappings on this integration use, `resultTests` the tests
 * that send results there: a PDF goes out alone, under its own code.
 */
export function pdfDraftErrors(
  draft: PdfMappingDraft,
  pdfTests: readonly string[],
  resultTests: readonly string[],
): Partial<Record<keyof PdfMappingDraft, string>> {
  const errors: Partial<Record<keyof PdfMappingDraft, string>> = {}
  const test = draft.testCode.trim()
  if (!RESULT_CODE_PATTERN.test(test)) errors.testCode = '1–20 letters or digits, e.g. CTPROCRPT.'
  else if (pdfTests.includes(test)) errors.testCode = `Another PDF mapping already uses ${test}.`
  else if (resultTests.includes(test)) {
    errors.testCode = `${test} already sends test results. Give the PDF its own order code, e.g. CTPROCRPT.`
  }
  if (draft.testName.trim().length > PDF_TEXT_MAX) errors.testName = `At most ${PDF_TEXT_MAX} characters.`
  if (!draft.formDefinitionId) errors.formDefinitionId = 'Choose the form the PDF is attached to.'
  if (!draft.fieldId) errors.fieldId = 'Choose the field the PDF is attached to.'
  if (!RESULT_CODE_PATTERN.test(draft.code.trim())) errors.code = '1–20 letters or digits, e.g. CTPDF.'
  if (draft.text.trim().length > PDF_TEXT_MAX) errors.text = `At most ${PDF_TEXT_MAX} characters.`
  const provider = providerProblem(draft.provider)
  if (provider) errors.provider = provider
  return errors
}

export function pdfDraftBody(draft: PdfMappingDraft): LisPdfMappingBody {
  return {
    testCode: draft.testCode.trim(),
    testName: draft.testName.trim() || null,
    sourceType: 'form_attachment',
    formDefinitionId: draft.formDefinitionId,
    fieldId: draft.fieldId,
    code: draft.code.trim(),
    text: draft.text.trim() || null,
    orderingProvider: providerDraftBody(draft.provider),
  }
}

/** 1.2 MB, 340 KB, 12 bytes. */
export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`
  return `${bytes} bytes`
}

export interface PdfPreviewView {
  /** File name, size and pages, as far as they are known. */
  details: string
  /** Why it cannot be sent, or null. */
  problem: string | null
  canSend: boolean
}

/**
 * The PDF part of the confirmation (AC2.1, AC2.2): what the file is, and
 * whether it can go. A missing file, a file that is not a PDF and one over the
 * size limit all come back from the backend as `problem`, and block Send.
 */
export function pdfPreviewView(doc: LisReleaseDocument): PdfPreviewView {
  const parts = [
    doc.fileName ?? 'No file',
    doc.sizeBytes !== null ? formatFileSize(doc.sizeBytes) : null,
    doc.pageCount !== null ? `${doc.pageCount} page${doc.pageCount === 1 ? '' : 's'}` : null,
  ].filter((p): p is string => !!p)
  return { details: parts.join(' · '), problem: doc.problem, canSend: doc.problem === null }
}

/** The file a release sent, for the history (AC2.4); null for a results release. */
export function releasedFileLabel(payload: { document?: { fileName: string; sizeBytes: number } | null } | undefined) {
  const doc = payload?.document
  return doc ? `${doc.fileName} (${formatFileSize(doc.sizeBytes)})` : null
}
